import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMe } from '@/auth/useAuth';

const draftSchema = z.object({
  text: z.string().trim().min(1).max(2000),
  candidate: z.boolean(),
  share: z.array(z.string()).max(30),
});
type Recipient = { kind: 'kid' | 'parent'; id: string; label: string };
type Entry = {
  id: string;
  text: string;
  source: 'self' | 'parent';
  updated_at: string;
  confirmation?: 'candidate' | 'confirmed';
  source_ref?: { type: string; actor_kind?: 'kid' | 'parent'; actor_id?: string; occurred_at?: string };
  shared_with?: Omit<Recipient, 'label'>[];
};
type Snapshot = {
  entries: Entry[];
  shared_entries?: (Entry & { owner: { kind: 'kid' | 'parent'; id: string } })[];
  revision: number;
  available: boolean;
  enabled: boolean;
  updated_at: string;
};
export function MemoryPanel({ kidId }: { kidId?: string }) {
  const me = useMe();
  return (
    <AccountMemoryPanel key={`${me.data?.kind}:${me.data?.sub}:${kidId ?? 'self'}`} kidId={kidId} />
  );
}

function AccountMemoryPanel({ kidId }: { kidId?: string }) {
  const me = useMe();
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof draftSchema>>({
    resolver: zodResolver(draftSchema),
    defaultValues: { text: '', candidate: false, share: [] },
  });
  const text = form.watch('text');
  const candidate = form.watch('candidate');
  const share = form.watch('share');
  const setText = (value: string) => form.setValue('text', value);
  const setCandidate = (value: boolean) => form.setValue('candidate', value);
  const setShare = (value: string[]) => form.setValue('share', value);
  const [clearing, setClearing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    operation_id: string;
    base_revision: number;
    entries: Entry[];
    delete_only?: boolean;
  } | null>(null);
  const principal = me.data?.kind === 'kid' ? 'kid' : 'user';
  const queryKey = ['desktop-memory', me.data?.sub, principal, kidId ?? 'self'];
  const suffix = kidId ? `?kid_id=${encodeURIComponent(kidId)}` : '';
  const connectionEpoch = useRef(0);
  const authorizedEpoch = useRef<number | null>(null);
  const [sharedAuthorized, setSharedAuthorized] = useState(false);
  const memory = useQuery({
    queryKey,
    queryFn: async () => {
      const epoch = connectionEpoch.current;
      const startedOnline = navigator.onLine;
      try {
        const value = await api<Snapshot>(`/desktop/memories${suffix}`, { principal });
        authorizedEpoch.current =
          epoch === connectionEpoch.current && startedOnline && navigator.onLine ? epoch : null;
        return value;
      } catch (error) {
        if (epoch === connectionEpoch.current) {
          authorizedEpoch.current = null;
          setSharedAuthorized(false);
        }
        throw error;
      }
    },
    enabled: Boolean(me.data),
    refetchInterval: 30_000,
  });
  useEffect(() => {
    if (
      memory.dataUpdatedAt > 0 &&
      authorizedEpoch.current === connectionEpoch.current &&
      navigator.onLine
    ) {
      setSharedAuthorized(true);
    }
  }, [memory.dataUpdatedAt]);
  const refetchMemory = memory.refetch;
  useEffect(() => {
    const offline = () => {
      connectionEpoch.current++;
      authorizedEpoch.current = null;
      setSharedAuthorized(false);
    };
    const online = () => {
      connectionEpoch.current++;
      authorizedEpoch.current = null;
      setSharedAuthorized(false);
      void refetchMemory();
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [refetchMemory]);
  const sharedEntries = sharedAuthorized && navigator.onLine ? memory.data?.shared_entries ?? [] : [];
  const recipients = useQuery({
    queryKey: [...queryKey, 'recipients'],
    queryFn: () => api<Recipient[]>(`/desktop/memories/recipients${suffix}`, { principal: 'user' }),
    enabled: me.data?.kind === 'user',
  });
  const deletions = useQuery({
    queryKey: [...queryKey, 'deletions', memory.data?.revision],
    queryFn: () =>
      api<{ id: string; backup_status: string; entry_ids: string[] }[]>(
        `/desktop/memories/deletions${suffix}`,
        { principal },
      ),
    enabled: Boolean(me.data),
    refetchInterval: 30_000,
  });
  const contextReceipts = useQuery({
    queryKey: [...queryKey, 'context-receipts'],
    queryFn: () => api<{ id: string; turn_id: string; sent_at: string; entry_ids: string[] }[]>(
      `/desktop/memories/context-receipts${suffix}`, { principal }),
    enabled: Boolean(me.data),
    refetchInterval: 30_000,
  });
  const save = useMutation({
    mutationFn: (operation: NonNullable<typeof pending>) =>
      api(`/desktop/memories${suffix}`, { method: 'POST', body: operation, principal }),
    onSuccess: async () => {
      setPending(null);
      setText('');
      setEditing(null);
      setShare([]);
      setCandidate(false);
      await qc.invalidateQueries({ queryKey });
    },
  });
  const consent = useMutation({
    mutationFn: (enabled: boolean) =>
      api<Snapshot>(`/desktop/memories/consent${suffix}`, {
        method: 'PUT',
        body: { enabled },
        principal: 'user',
      }),
    onSuccess: (data) => qc.setQueryData(queryKey, data),
  });
  function write(entries: Entry[], deleteOnly = false) {
    if (!memory.data || pending) return;
    const operation = {
      operation_id: crypto.randomUUID(),
      base_revision: memory.data.revision,
      entries,
      ...(deleteOnly ? { delete_only: true } : {}),
    };
    setPending(operation);
    save.mutate(operation);
  }
  const reviewKey = `airbotix:memory-reviewed:${me.data?.kind}:${me.data?.sub}:${kidId ?? 'self'}`;
  const [reviewed, setReviewed] = useState<string[]>(() => {
    try {
      const value: unknown = JSON.parse(localStorage.getItem(reviewKey) ?? '[]');
      return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string').slice(0, 100) : [];
    } catch { return []; }
  });
  const parentUpdates = me.data?.kind === 'kid'
    ? (memory.data?.entries ?? []).filter(entry => entry.source === 'parent' && !reviewed.includes(`${entry.id}:${entry.updated_at}`))
    : [];
  const reviewParentUpdates = () => {
    const markers = (memory.data?.entries ?? []).filter(entry => entry.source === 'parent')
      .map(entry => `${entry.id}:${entry.updated_at}`);
    setReviewed(markers);
    try { localStorage.setItem(reviewKey, JSON.stringify(markers)); } catch { /* Review markers remain in memory for this session. */ }
  };
  const recipientChoices = recipients.isError ? undefined : recipients.data;
  const currentSharing = memory.data?.entries.find(entry => entry.id === editing)?.shared_with ?? [];
  const sharingNeedsConnection = me.data?.kind === 'user' && !recipientChoices &&
    JSON.stringify([...share].sort()) !== JSON.stringify(currentSharing.map(target => `${target.kind}:${target.id}`).sort());
  const busy = save.isPending || consent.isPending;
  return (
    <section className="card-base space-y-4 p-5" data-testid="memory-panel">
      <h2 className="text-xl font-bold text-ink">{kidId ? 'Student memory' : 'My memory'}</h2>
      <p className="text-slate2">
        Personal memory follows the Airbotix account across Hangar computers. It does not change
        grades or Stars.
      </p>
      {memory.isPending && <p role="status">Loading memory…</p>}
      {memory.isError && (
        <p role="alert">
          Could not load memory.{' '}
          <button className="btn-pill-ghost" onClick={() => void memory.refetch()}>
            Try again
          </button>
        </p>
      )}
      {(save.isError || consent.isError) && (
        <p role="alert">{(save.error ?? consent.error)?.message ?? 'Could not save memory.'}</p>
      )}
      {pending && save.isError && (
        <div>
          <button
            className="btn-pill-secondary"
            disabled={busy}
            onClick={() => save.mutate(pending)}
          >
            Retry saved change
          </button>
          <button
            className="btn-pill-ghost"
            disabled={busy}
            onClick={() => {
              setPending(null);
              void memory.refetch();
            }}
          >
            Review cloud version
          </button>
        </div>
      )}
      {memory.data && !memory.isError && (
        <>
          <p role="status">
            {memory.data.available
              ? memory.data.enabled
                ? 'Cloud sync enabled'
                : 'Cloud sync paused'
              : 'Cloud sync is not yet available'}
          </p>
          {me.data?.kind === 'user' && (
            <button
              className="btn-pill-secondary"
              disabled={busy}
              onClick={() => consent.mutate(!memory.data!.enabled)}
            >
              {memory.data.enabled ? 'Pause cloud sync' : 'Enable cloud sync'}
            </button>
          )}
          <p className="text-sm text-slate2">
            Pausing stops new uploads. Existing cloud memories remain until you delete them.
          </p>
          {memory.data.entries.length > 0 && (
            <div>
              {clearing ? (
                <>
                  <p>Delete every saved cloud memory item for this account?</p>
                  <button
                    className="btn-pill-secondary"
                    disabled={busy || Boolean(pending)}
                    onClick={() => {
                      setClearing(false);
                      write([], true);
                    }}
                  >
                    Confirm deletion of all cloud memory
                  </button>
                  <button className="btn-pill-ghost" onClick={() => setClearing(false)}>
                    Cancel deletion
                  </button>
                </>
              ) : (
                <button
                  className="btn-pill-ghost"
                  disabled={busy || Boolean(pending)}
                  onClick={() => setClearing(true)}
                >
                  Delete all cloud memory
                </button>
              )}
            </div>
          )}
          {parentUpdates.length > 0 && (
            <div className="rounded-2xl bg-canvas p-4" role="status">
              <p>Your parent updated {parentUpdates.length} memory item(s). Review the parent corrections below.</p>
              <button className="btn-pill-secondary" onClick={reviewParentUpdates}>I have reviewed these updates</button>
            </div>
          )}
          {!memory.data.entries.length && <p>No saved memory yet.</p>}
          <ul className="space-y-3">
            {memory.data.entries.map((entry) => (
              <li className="rounded-2xl bg-canvas p-4" key={entry.id}>
                <p>{entry.text}</p>
                <p className="text-sm text-slate2">
                  Source:{' '}
                  {entry.source_ref?.type?.replaceAll('_', ' ') ??
                    (entry.source === 'parent' ? 'Parent correction' : 'Account owner')}{' '}
                  ·{' '}
                  {entry.confirmation === 'candidate'
                    ? 'Awaiting confirmation; excluded from AI'
                    : 'Confirmed; available to AI when sync is enabled'}
                  {entry.shared_with?.length
                    ? ` · Shared with ${entry.shared_with.length} family member(s)`
                    : ' · Private'}
                </p>
                <p className="text-sm text-slate2">Updated: {new Date(entry.updated_at).toLocaleString()}</p>
                <details>
                  <summary>Why this memory may be used</summary>
                  <p>{entry.confirmation === 'candidate'
                    ? 'This suggestion is waiting for confirmation and is excluded from AI context.'
                    : !memory.data.enabled || !memory.data.available
                      ? 'Cloud memory is paused or unavailable, so this account’s memory is excluded from the helper.'
                      : 'This confirmed memory belongs to the selected account. The helper may use it to continue learning, subject to privacy and safety checks. It is not necessarily used in every reply.'}</p>
                </details>
                {entry.confirmation === 'candidate' && (
                  <button
                    className="btn-pill-secondary"
                    disabled={
                      busy || Boolean(pending) || !memory.data.enabled || !memory.data.available
                    }
                    onClick={() =>
                      write(
                        memory.data!.entries.map((item) =>
                          item.id === entry.id ? { ...item, confirmation: 'confirmed' } : item,
                        ),
                      )
                    }
                  >
                    Confirm memory
                  </button>
                )}
                <button
                  className="btn-pill-ghost"
                  disabled={busy || Boolean(pending)}
                  onClick={() =>
                    write(
                      memory.data!.entries.filter((item) => item.id !== entry.id),
                      true,
                    )
                  }
                >
                  Delete memory
                </button>
                <button
                  className="btn-pill-ghost"
                  disabled={
                    busy || Boolean(pending) || !memory.data.enabled || !memory.data.available
                  }
                  onClick={() => {
                    setEditing(entry.id);
                    setText(entry.text);
                    setCandidate(entry.confirmation === 'candidate');
                    setShare(
                      (entry.shared_with ?? []).map((target) => `${target.kind}:${target.id}`),
                    );
                  }}
                >
                  Edit memory
                </button>
              </li>
            ))}
          </ul>
          {sharedEntries.length > 0 && (
            <section aria-label="Shared family memory">
              <h3>Shared with you</h3>
              <ul>
                {sharedEntries.map((entry) => (
                  <li
                    key={`${entry.owner.kind}:${entry.owner.id}:${entry.id}`}
                    className="rounded-2xl bg-canvas p-4"
                  >
                    <p>{entry.text}</p>
                    <p className="text-sm text-slate2">
                      Confirmed family memory. The owner controls sharing and correction.
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {deletions.isError && <p role="alert">Deletion status could not be loaded.</p>}
          {Boolean(deletions.data?.length) && (
            <details>
              <summary>Cloud deletion status</summary>
              <ul>
                {deletions.data!.map((item) => (
                  <li key={item.id}>
                    {item.entry_ids.length} memory item(s) removed from cloud storage ·{' '}
                    {item.backup_status === 'provider_verified' ? 'Backup provider verified completion' :
                      item.backup_status === 'provider_pending' ? 'Backup provider handling pending' :
                      item.backup_status === 'provider_failed' ? 'Backup provider handling needs review' :
                      ['verified', 'operator_attested'].includes(item.backup_status) ? 'Operator backup evidence recorded' : 'Backup handling pending'}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <section aria-label="Recent helper context" className="rounded-2xl bg-canvas p-4">
            <h3 className="font-bold text-ink">Recent helper context</h3>
            <p>These memory items were provided with recent helper requests. The helper may not mention or adopt them. Current item versions are shown below and may have changed since the request. An interrupted reply can still be refunded.</p>
            {contextReceipts.isPending && <p role="status">Loading recent context…</p>}
            {contextReceipts.isError && <p role="alert">Recent helper context could not be loaded. <button className="btn-pill-ghost" onClick={() => void contextReceipts.refetch()}>Try again</button></p>}
            {!contextReceipts.isError && contextReceipts.data?.length === 0 && <p>No saved-memory context receipts yet.</p>}
            {!contextReceipts.isError && contextReceipts.data?.map(receipt => (
              <details key={receipt.id}>
                <summary>{new Date(receipt.sent_at).toLocaleString()} · {receipt.entry_ids.length} memory item(s)</summary>
                <ul>{receipt.entry_ids.map(id => (
                  <li key={id}>Current item: {memory.data!.entries.find(entry => entry.id === id)?.text ??
                    sharedEntries.find(entry => entry.id === id)?.text ?? 'A previous memory item that is now removed or unavailable.'}</li>
                ))}</ul>
              </details>
            ))}
          </section>
          <form
            onSubmit={form.handleSubmit(() => {
              if (text.trim())
                write(
                  editing
                    ? memory.data!.entries.map((entry) =>
                        entry.id === editing
                          ? {
                              ...entry,
                              text: text.trim(),
                              confirmation: candidate ? 'candidate' : 'confirmed',
                              ...(me.data?.kind === 'user' && recipientChoices
                                ? {
                                    shared_with: (recipientChoices ?? [])
                                      .filter((target) =>
                                        share.includes(`${target.kind}:${target.id}`),
                                      )
                                      .map(({ kind, id }) => ({ kind, id })),
                                  }
                                : {}),
                            }
                          : entry,
                      )
                    : [
                        ...memory.data!.entries,
                        {
                          id: crypto.randomUUID(),
                          text: text.trim(),
                          source: 'self',
                          confirmation: candidate ? 'candidate' : 'confirmed',
                          shared_with: (recipientChoices ?? [])
                            .filter((target) => share.includes(`${target.kind}:${target.id}`))
                            .map(({ kind, id }) => ({ kind, id })),
                          updated_at: new Date().toISOString(),
                        },
                      ],
                );
            })}
          >
            <label className="block text-sm font-semibold text-ink" htmlFor="saved-memory">
              {editing ? 'Correct memory' : 'Remember this'}
            </label>
            <textarea
              id="saved-memory"
              className="input-field mt-2 w-full"
              maxLength={2000}
              value={text}
              onChange={(event) => setText(event.target.value)}
              required
            />
            <label className="block mt-3">
              <input
                type="checkbox"
                checked={candidate}
                onChange={(event) => setCandidate(event.target.checked)}
              />{' '}
              Save an AI suggestion for review before use
            </label>
            {me.data?.kind === 'user' && (
              <fieldset className="mt-3">
                <legend>Share this memory with</legend>
                <p className="text-sm text-slate2">
                  Only selected family members receive confirmed memory. Clear every selection to
                  make it private.
                </p>
                {recipients.isError && (
                  <p role="alert">
                    Family recipients could not be loaded. Sharing cannot be changed.
                  </p>
                )}
                {recipientChoices?.map((target) => {
                  const key = `${target.kind}:${target.id}`;
                  return (
                    <label key={key} className="block">
                      <input
                        type="checkbox"
                        checked={share.includes(key)}
                        onChange={(event) =>
                          setShare(
                            event.target.checked
                              ? [...share, key]
                              : share.filter((item) => item !== key),
                          )
                        }
                      />{' '}
                      {target.label}
                    </label>
                  );
                })}
              </fieldset>
            )}
            {form.formState.errors.text && <p role="alert">{form.formState.errors.text.message}</p>}
            <button
              className="btn-pill-primary mt-3"
              disabled={
                busy ||
                Boolean(pending) ||
                !memory.data.enabled ||
                !memory.data.available ||
                (memory.data.entries.length >= 100 && !editing) ||
                sharingNeedsConnection
              }
            >
              {editing ? 'Save correction' : 'Save memory'}
            </button>
            {editing && (
              <button
                type="button"
                className="btn-pill-ghost"
                onClick={() => {
                  setEditing(null);
                  setText('');
                }}
              >
                Cancel correction
              </button>
            )}
          </form>
        </>
      )}
    </section>
  );
}
